import {
  CarOutlined, ClockCircleOutlined, ExclamationCircleOutlined, PlusOutlined,
  SearchOutlined, UserOutlined
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, Empty, Input, Spin, Tag, Typography } from "antd";
import { useDeferredValue, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatDateTime, formatMileage, formatPhoneInput, isPhoneSearchTerm } from "../../shared/utils/formatters";
import { customerName } from "../customers/customer-types";
import { repairOrdersApi } from "../repair-orders/repair-orders-api";
import type {
  ArrivalBoardItem, ArrivalSearchResponse, RepairEventAttentionState
} from "../repair-orders/repair-order-types";
import { ScanButton } from "../scan/ScanButton";
import { useTimedNoResults } from "../../shared/hooks/useTimedNoResults";

const ATTENTION_COLUMNS: Array<{
  key: string;
  label: string;
  states: RepairEventAttentionState[];
}> = [
  { key: "inspection", label: "attentionWaitingInspection", states: ["DRAFT", "WAITING_INSPECTION"] },
  { key: "estimate", label: "attentionWaitingEstimate", states: ["WAITING_ESTIMATE"] },
  { key: "approval", label: "attentionWaitingApproval", states: ["WAITING_CUSTOMER_APPROVAL"] },
  { key: "parts", label: "attentionWaitingParts", states: ["WAITING_PARTS"] },
  { key: "workshop", label: "attentionWorkshop", states: ["WAITING_TECHNICIAN", "WORK_IN_PROGRESS"] },
  { key: "qc", label: "attentionWaitingQc", states: ["WAITING_QC"] },
  { key: "payment", label: "attentionPaymentPickup", states: ["WAITING_PAYMENT", "READY_PICKUP", "READY_TO_CLOSE"] }
];

export function FrontDeskPage() {
  const { text } = useAppConfig();
  const labels = text as unknown as Record<string, string>;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim());
  const normalizedSearch = isPhoneSearchTerm(deferredSearch)
    ? formatPhoneInput(deferredSearch)
    : deferredSearch;

  const board = useQuery({
    queryKey: ["front-desk-v2-board"],
    queryFn: repairOrdersApi.arrivalBoard,
    refetchInterval: 30_000
  });
  const searchResults = useQuery({
    queryKey: ["front-desk-v2-search", normalizedSearch],
    enabled: normalizedSearch.length >= 2,
    queryFn: () => repairOrdersApi.arrivalSearch(normalizedSearch)
  });
  const resultCount = searchResults.data
    ? searchResults.data.repairEvents.length
      + searchResults.data.vehicles.length
      + searchResults.data.customers.length
    : 0;
  const hasResults = resultCount > 0;
  const noResultsDismissed = useTimedNoResults({
    query: normalizedSearch,
    enabled: normalizedSearch.length >= 2,
    loading: searchResults.isFetching,
    ready: Boolean(searchResults.data),
    hasResults
  });
  const startNew = () => navigate(search.trim()
    ? `/repair-orders/new?lookup=${encodeURIComponent(search.trim())}`
    : "/repair-orders/new");
  const openOnlyResult = () => {
    const data = searchResults.data;
    if (!data || resultCount !== 1) return;
    if (data.repairEvents[0]) return navigate(data.repairEvents[0].attention.workspacePath);
    if (data.vehicles[0]) return navigate(vehicleAction(data.vehicles[0]));
    if (data.customers[0]) return navigate(`/repair-orders/new?customerId=${data.customers[0].id}`);
  };

  return <section className="front-desk-page front-desk-v2">
    <div className="page-title front-desk-title">
      <div>
        <Typography.Text type="secondary">{text.today}</Typography.Text>
        <Typography.Title level={2}>{text.frontDesk}</Typography.Title>
        <Typography.Paragraph type="secondary">{text.frontDeskV2Hint}</Typography.Paragraph>
      </div>
      <Tag color="blue">{text.target60Seconds}</Tag>
    </div>

    <div className="front-desk-command-row">
      <div className="global-search-wrap">
        <Input
          size="large"
          allowClear
          autoFocus
          prefix={<SearchOutlined />}
          placeholder={text.globalSearchPlaceholder}
          value={search}
          onChange={event => setSearch(event.target.value.toUpperCase())}
          onPressEnter={openOnlyResult}
        />
        {deferredSearch.length >= 2 && !noResultsDismissed && <Card className="global-search-results">
          {searchResults.isFetching
            ? <div className="search-loading"><Spin size="small" /> {text.searching}</div>
            : hasResults
              ? <ArrivalSearchGroups data={searchResults.data!} navigate={navigate} text={labels} />
              : <div className="arrival-no-result">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={text.noSearchResults} />
                <Typography.Text type="secondary">{text.searchValueWillBeReused}</Typography.Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={startNew}>{text.startNewRepairEvent}</Button>
              </div>}
        </Card>}
      </div>
      <ScanButton />
      <Button type="primary" size="large" icon={<PlusOutlined />} onClick={startNew}>
        {text.newRepairEvent}
      </Button>
    </div>

    <div className="arrival-attention-strip">
      <AttentionMetric label={text.activeVehicles} value={board.data?.total ?? 0} />
      <AttentionMetric label={text.overdueAttention} value={board.data?.overdue ?? 0} alert={Boolean(board.data?.overdue)} />
      <AttentionMetric label={text.promiseRisk} value={board.data?.promiseRisk ?? 0} alert={Boolean(board.data?.promiseRisk)} />
    </div>

    <div className="board-heading">
      <div>
        <Typography.Title level={3}>{text.todaysVehicles}</Typography.Title>
        <Typography.Text type="secondary">{text.nextActionBoardHint}</Typography.Text>
      </div>
      {board.isFetching && <Spin size="small" />}
    </div>

    <div className="repair-kanban arrival-board" aria-label={text.todaysVehicles}>
      {ATTENTION_COLUMNS.map(column => {
        const orders = (board.data?.items ?? []).filter(order => column.states.includes(order.attention.state));
        return <section className="kanban-column" key={column.key}>
          <header><span>{labels[column.label]}</span><Tag>{orders.length}</Tag></header>
          <div className="kanban-cards">
            {orders.map(order => <ArrivalCard
              key={order.id}
              order={order}
              onOpen={() => navigate(order.attention.workspacePath)}
              text={labels}
            />)}
            {!orders.length && <div className="kanban-empty">{text.noVehiclesWaiting}</div>}
          </div>
        </section>;
      })}
    </div>
  </section>;
}

function ArrivalSearchGroups({ data, navigate, text }: {
  data: ArrivalSearchResponse;
  navigate: (path: string) => void;
  text: Record<string, string>;
}) {
  return <>
    <SearchGroup title={text.activeRepairEvents ?? ""} icon={<ClockCircleOutlined />} items={data.repairEvents.map(order => ({
      key: order.id,
      title: `${order.roNumber} · ${text[order.attention.nextAction] ?? order.attention.nextAction}`,
      subtitle: `${order.vehicle.year} ${order.vehicle.make} ${order.vehicle.model} · ${customerName(order.customer)}`,
      tag: text[`attention${order.attention.state}`] ?? order.attention.state.replaceAll("_", " "),
      action: () => navigate(order.attention.workspacePath)
    }))} />
    <SearchGroup title={text.vehicles ?? ""} icon={<CarOutlined />} items={data.vehicles.map(vehicle => ({
      key: vehicle.id,
      title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
      subtitle: `${vehicle.vin} · ${customerName(vehicle.customer)}`,
      tag: (vehicle.activeRepairEvent ? text.continueActiveRepair : text.startNewRepairEvent) ?? "",
      action: () => navigate(vehicleAction(vehicle))
    }))} />
    <SearchGroup title={text.customers ?? ""} icon={<UserOutlined />} items={data.customers.map(customer => ({
      key: customer.id,
      title: customerName(customer),
      subtitle: customer.phone,
      tag: text.selectVehicleNext ?? "",
      action: () => navigate(`/repair-orders/new?customerId=${customer.id}`)
    }))} />
  </>;
}

function vehicleAction(vehicle: ArrivalSearchResponse["vehicles"][number]) {
  return vehicle.activeRepairEvent?.workspacePath
    ?? `/repair-orders/new?customerId=${vehicle.customerId}&vehicleId=${vehicle.id}`;
}

function SearchGroup({ title, icon, items }: {
  title: string;
  icon: ReactNode;
  items: Array<{ key: string; title: string; subtitle: string; tag: string; action: () => void }>;
}) {
  if (!items.length) return null;
  return <section className="search-result-group">
    <header>{icon}{title}</header>
    {items.map(item => <button type="button" key={item.key} onClick={item.action}>
      <span><b>{item.title}</b><small>{item.subtitle}</small></span>
      <Tag>{item.tag}</Tag>
    </button>)}
  </section>;
}

function AttentionMetric({ label, value, alert = false }: { label: string; value: number; alert?: boolean }) {
  return <Card size="small" className={alert ? "attention-metric alert" : "attention-metric"}>
    <span>{alert && <ExclamationCircleOutlined />}{label}</span>
    <b>{value}</b>
  </Card>;
}

function ArrivalCard({ order, onOpen, text }: {
  order: ArrivalBoardItem;
  onOpen: () => void;
  text: Record<string, string>;
}) {
  const attention = order.attention;
  return <button type="button" className={`repair-kanban-card arrival-card${attention.isOverdue ? " overdue" : ""}`} onClick={onOpen}>
    <div className="kanban-card-top">
      <b>{order.roNumber}</b>
      <Tag color={attention.isOverdue ? "red" : attention.promiseRisk ? "orange" : "blue"}>
        {text[`attention${attention.state}`] ?? attention.state.replaceAll("_", " ")}
      </Tag>
    </div>
    <strong>{order.vehicle.year} {order.vehicle.make} {order.vehicle.model}</strong>
    <span>{customerName(order.customer)}</span>
    <span className="mono">{order.vehicle.licensePlate || order.vehicle.vin}</span>
    <div className="arrival-next-action">
      <small>{text.nextAction}</small>
      <b>{text[attention.nextAction] ?? attention.nextAction}</b>
      <span>{attention.owner?.displayName ?? text[`role${attention.ownerRole}`] ?? attention.ownerRole}</span>
    </div>
    <div className="kanban-card-meta">
      <span>{formatWaiting(attention.waitingMinutes ?? 0, text)}</span>
      <span>{formatMileage(order.mileageIn)}</span>
      <span>{order.promisedAt ? formatDateTime(order.promisedAt) : text.noPromiseTime}</span>
    </div>
  </button>;
}

function formatWaiting(minutes: number, text: Record<string, string>) {
  if (minutes < 60) return `${minutes} ${text.minutesWaiting}`;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  return `${hours}h ${remainder}m ${text.waiting}`;
}
